(function () {
  'use strict';

  const ELEMENT_NAME = 'scp-steering-wheel-designer';
  const PRELOAD_LIMIT = 4;

  function parseJson(element) {
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      return null;
    }
  }

  function slug(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function arraysEqual(left, right) {
    return left.length === right.length && left.every(function (value, index) {
      return value === right[index];
    });
  }

  function createBuildId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    return 'sw-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  class SCPDesigner extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        window.addEventListener('popstate', this.boundPopState);
        return;
      }

      this.initialized = true;
      this.configElement = this.querySelector('[data-designer-config]');
      this.config = this.configElement ? parseJson(this.configElement) : null;

      if (!this.isConfigValid()) {
        this.setAttribute('data-designer-error', 'configuration');
        return;
      }

      this.products = Array.isArray(this.config.products) && this.config.products.length
        ? this.config.products
        : [this.config.product];
      const serverProductHandle = this.config.product.handle;
      this.displayedProductHandle = serverProductHandle;
      const requestedWheel = new URL(window.location.href).searchParams.get('wheel');
      this.hasExplicitWheel = this.products.some(function (product) { return product.handle === requestedWheel; });
      this.config.product = this.products.find(function (product) {
        return product.handle === requestedWheel;
      }) || this.products[0];
      this.cacheElements();
      if (this.config.product.handle !== serverProductHandle) {
        this.rebuildProductControls();
        this.cacheElements();
      }
      this.preloaded = new Set();
      this.previewRequest = 0;
      this.maskRequest = 0;
      this.maskAssets = new Map();
      this.maskAlphas = new Map();
      this.state = this.createInitialState();
      this.state.stage = this.hasExplicitWheel ? 'design' : 'manufacturer';
      this.state.manufacturer = slug(this.config.product.brand || '');
      this.bindEvents();
      this.buildManufacturerOptions();
      this.render({ replaceUrl: false, announceInvalidUrl: this.state.invalidUrl });
      this.dispatchDesignerEvent('opened', { productHandle: this.config.product.handle });

      if (this.debugEnabled()) {
        this.debugCatalog();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.boundPopState);
      this.previewRequest++;
      this.maskRequest++;
    }

    isConfigValid() {
      const product = this.config && this.config.product;
      const products = this.config && this.config.products;

      return Boolean(
        product &&
        product.id &&
        product.handle &&
        Array.isArray(product.options) &&
        product.options.length > 0 &&
        Array.isArray(product.variants) &&
        product.variants.length > 0 &&
        product.variants.every(function (variant) {
          return variant.id && Array.isArray(variant.options) && variant.options.length === product.options.length;
        }) &&
        (!products || !products.length || products.every(function (candidate) {
          return candidate.id && candidate.handle && Array.isArray(candidate.options) && candidate.options.length > 0 &&
            Array.isArray(candidate.variants) && candidate.variants.length > 0 && candidate.variants.every(function (variant) {
              return variant.id && Array.isArray(variant.options) && variant.options.length === candidate.options.length;
            });
        }))
      );
    }

    cacheElements() {
      this.currentImage = this.querySelector('[data-designer-preview-current]');
      this.bufferImage = this.querySelector('[data-designer-preview-buffer]');
      this.previewPlaceholder = this.querySelector('[data-designer-preview-placeholder]');
      this.maskCanvas = this.querySelector('[data-designer-mask-canvas]');
      this.maskFeedback = this.querySelector('[data-designer-mask-feedback]');
      this.priceElement = this.querySelector('[data-designer-price]');
      this.comparePriceElement = this.querySelector('[data-designer-compare-price]');
      this.availabilityElement = this.querySelector('[data-designer-availability]');
      this.addButton = this.querySelector('[data-designer-add]');
      this.addLabel = this.querySelector('[data-designer-add-label]');
      this.addSpinner = this.querySelector('[data-designer-add-spinner]');
      this.messageElement = this.querySelector('[data-designer-message]');
      this.wheelNameElement = this.querySelector('[data-designer-wheel-name]');
      this.wheelNoteElement = this.querySelector('[data-designer-wheel-note]');
      this.optionInputs = Array.from(this.querySelectorAll('[data-designer-option]'));
      this.viewInputs = Array.from(this.querySelectorAll('[data-designer-view]'));
      this.stageTabs = Array.from(this.querySelectorAll('[data-designer-stage]'));
      this.stagePanels = Array.from(this.querySelectorAll('[data-designer-stage-panel]'));
      this.hotspotButtons = Array.from(this.querySelectorAll('[data-designer-hotspot]'));
      this.comparison = this.querySelector('[data-designer-comparison]');
      this.comparisonToggle = this.querySelector('[data-designer-comparison-toggle]');
      this.comparisonRange = this.querySelector('[data-designer-comparison-range]');
    }

    createInitialState() {
      const defaultVariant = this.config.product.variants.find(function (variant) {
        return variant.available && variant.featuredImage;
      }) || this.config.product.variants.find(function (variant) {
        return variant.available;
      }) || this.config.product.variants[0];
      const parsed = this.parseUrl(defaultVariant.options);
      const configuredView = this.config.settings.defaultView;
      const defaultView = this.isViewEnabled(configuredView) ? configuredView : this.firstEnabledView();

      return {
        options: parsed.options,
        view: parsed.view || defaultView,
        variant: null,
        invalidUrl: parsed.invalid,
        submitting: false,
        hotspot: null,
        comparing: false
      };
    }

    firstEnabledView() {
      return this.config.settings.enableInstalledView ? 'installed' : 'studio';
    }

    isViewEnabled(view) {
      return view === 'installed'
        ? this.config.settings.enableInstalledView
        : view === 'studio' && this.config.settings.enableStudioView;
    }

    parseUrl(defaultOptions) {
      const params = new URL(window.location.href).searchParams;
      const options = defaultOptions.slice();
      let invalid = false;

      if (params.has('wheel') && params.get('wheel') !== this.config.product.handle) {
        invalid = true;
      }

      this.config.product.options.forEach((optionName, index) => {
        const key = this.urlKeyForOption(optionName, index);
        const requested = params.get(key);
        if (!requested) return;

        const values = this.valuesForOption(index);
        const matches = values.filter(function (value) {
          return slug(value) === requested;
        });

        if (matches.length === 1) {
          options[index] = matches[0];
        } else {
          invalid = true;
        }
      });

      const requestedVariantId = params.get('variant');
      if (requestedVariantId && !this.config.product.variants.some(function (variant) {
        return String(variant.id) === requestedVariantId;
      })) invalid = true;

      const requestedView = params.get('designer_view');
      let view = null;
      if (requestedView) {
        if (this.isViewEnabled(requestedView)) view = requestedView;
        else invalid = true;
      }

      if (!this.findVariant(options)) {
        invalid = true;
        return { options: defaultOptions.slice(), view: view, invalid: invalid };
      }

      return { options: options, view: view, invalid: invalid };
    }

    urlKeyForOption(optionName, index) {
      const normalized = slug(optionName);
      if (normalized.indexOf('material') !== -1) return 'material';
      if (normalized.indexOf('stripe') !== -1) return 'stripe';
      if (normalized.indexOf('stitch') !== -1) return 'stitching';
      return 'option-' + (index + 1);
    }

    valuesForOption(index) {
      return this.config.product.variants.reduce(function (values, variant) {
        const value = variant.options[index];
        if (value && values.indexOf(value) === -1) values.push(value);
        return values;
      }, []);
    }

    findVariant(options) {
      return this.config.product.variants.find(function (variant) {
        return arraysEqual(variant.options, options);
      }) || null;
    }

    nearestVariantForOption(index, value) {
      const candidates = this.config.product.variants.filter(function (variant) {
        return variant.options[index] === value;
      });
      candidates.sort((left, right) => {
        const differences = (variant) => variant.options.reduce((count, option, optionIndex) =>
          count + (optionIndex !== index && option !== this.state.options[optionIndex] ? 1 : 0), 0);
        return differences(left) - differences(right) || Number(right.available) - Number(left.available);
      });
      return candidates[0] || null;
    }

    bindEvents() {
      this.addEventListener('click', (event) => {
        const wheelButton = event.target.closest('[data-designer-wheel]');
        const manufacturerButton = event.target.closest('[data-designer-manufacturer]');
        const stageButton = event.target.closest('[data-designer-stage]');
        const hotspotButton = event.target.closest('[data-designer-hotspot]');
        const optionTrigger = event.target.closest('[data-designer-option-trigger]');
        const comparisonToggle = event.target.closest('[data-designer-comparison-toggle]');

        if (hotspotButton) {
          const hotspotIndex = Number(hotspotButton.dataset.designerHotspot);
          const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
          this.state.hotspot = !canHover && this.state.hotspot === hotspotIndex ? null : hotspotIndex;
          this.state.comparing = false;
          this.renderInteractivePreview();
        }
        if (optionTrigger) {
          const optionIndex = Number(optionTrigger.dataset.designerOptionTrigger);
          this.state.hotspot = this.state.hotspot === optionIndex ? null : optionIndex;
          this.state.comparing = false;
          this.renderInteractivePreview();
        }
        if (comparisonToggle) {
          this.state.comparing = !this.state.comparing;
          this.state.hotspot = null;
          this.renderInteractivePreview();
        }

        if (manufacturerButton) {
          this.selectManufacturer(manufacturerButton.dataset.designerManufacturer);
          this.setStage('wheel');
        }
        if (wheelButton) {
          this.selectProduct(wheelButton.dataset.designerWheel);
          this.setStage('design');
        }
        if (stageButton) this.setStage(stageButton.dataset.designerStage);
      });

      this.addEventListener('keydown', (event) => {
        const activeTab = event.target.closest('[data-designer-stage]');
        if (!activeTab || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
        event.preventDefault();
        const currentIndex = this.stageTabs.indexOf(activeTab);
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const nextTab = this.stageTabs[(currentIndex + direction + this.stageTabs.length) % this.stageTabs.length];
        this.setStage(nextTab.dataset.designerStage);
        nextTab.focus();
      });

      this.addEventListener('change', (event) => {
        const optionInput = event.target.closest('[data-designer-option]');
        const viewInput = event.target.closest('[data-designer-view]');

        if (optionInput) {
          const optionIndex = Number(optionInput.dataset.designerOption);
          const nextVariant = this.nearestVariantForOption(optionIndex, optionInput.value);
          if (!nextVariant) {
            this.showMessage(this.config.strings.unavailable);
            this.renderOptions();
            return;
          }
          const adjusted = nextVariant.options.some((value, index) =>
            index !== optionIndex && value !== this.state.options[index]
          );
          this.state.options = nextVariant.options.slice();
          this.state.invalidUrl = false;
          this.showMessage(adjusted ? this.config.strings.adjustedOptions : '');
          this.render({ replaceUrl: true });
          this.dispatchDesignerEvent('option-changed', {
            option: this.config.product.options[optionIndex],
            value: optionInput.value,
            adjustedOptions: adjusted
          });
        }

        if (viewInput) {
          this.state.view = viewInput.value;
          this.render({ replaceUrl: true });
          this.dispatchDesignerEvent('view-changed', { view: this.state.view });
        }
      });

      this.addEventListener('input', (event) => {
        const comparisonRange = event.target.closest('[data-designer-comparison-range]');
        if (comparisonRange && this.comparison) {
          this.comparison.style.setProperty('--comparison-position', comparisonRange.value + '%');
        }
      });

      this.addEventListener('pointerover', (event) => {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        const hotspotButton = event.target.closest('[data-designer-hotspot]');
        if (!hotspotButton) return;
        this.state.hotspot = Number(hotspotButton.dataset.designerHotspot);
        this.state.comparing = false;
        this.renderInteractivePreview();
      });

      this.addEventListener('focusin', (event) => {
        const hotspotButton = event.target.closest('[data-designer-hotspot]');
        if (!hotspotButton) return;
        this.state.hotspot = Number(hotspotButton.dataset.designerHotspot);
        this.state.comparing = false;
        this.renderInteractivePreview();
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || this.state.hotspot === null) return;
        this.state.hotspot = null;
        this.renderInteractivePreview();
      });

      this.addButton.addEventListener('click', () => this.addToCart());
      this.boundPopState = () => {
        const requestedHandle = new URL(window.location.href).searchParams.get('wheel');
        const requestedProduct = this.products.find(function (product) {
          return product.handle === requestedHandle;
        });
        if (requestedProduct && requestedProduct.handle !== this.config.product.handle) {
          this.config.product = requestedProduct;
          this.rebuildProductControls();
          this.cacheElements();
        }
        this.state.manufacturer = slug(this.config.product.brand || '');
        this.buildManufacturerOptions();
        const popstateDefault = this.config.product.variants.find(function (variant) {
          return variant.available && variant.featuredImage;
        }) || this.config.product.variants.find(function (variant) {
          return variant.available;
        }) || this.config.product.variants[0];
        const parsed = this.parseUrl(popstateDefault.options);
        this.state.options = parsed.options;
        this.state.view = parsed.view || (this.isViewEnabled(this.config.settings.defaultView)
          ? this.config.settings.defaultView : this.firstEnabledView());
        this.state.invalidUrl = parsed.invalid;
        this.state.hotspot = null;
        this.state.comparing = false;
        this.state.stage = requestedProduct ? 'design' : 'manufacturer';
        this.render({ replaceUrl: false, announceInvalidUrl: parsed.invalid });
      };
      window.addEventListener('popstate', this.boundPopState);
    }

    buildManufacturerOptions() {
      const container = this.querySelector('[data-designer-manufacturers]');
      if (!container) return;

      const manufacturers = [];
      this.products.forEach(function (product) {
        const name = String(product.brand || '').trim() || 'Other';
        const handle = slug(name);
        if (!manufacturers.some(function (item) { return item.handle === handle; })) {
          manufacturers.push({ handle: handle, name: name });
        }
      });

      if (!container.querySelector('[data-designer-manufacturer]')) {
        container.replaceChildren();
        manufacturers.forEach((manufacturer) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'scp-designer__manufacturer-card';
          button.dataset.designerManufacturer = manufacturer.handle;
          button.setAttribute('aria-pressed', manufacturer.handle === this.state.manufacturer ? 'true' : 'false');
          const mark = document.createElement('span');
          mark.className = 'scp-designer__manufacturer-mark';
          mark.setAttribute('aria-hidden', 'true');
          mark.textContent = manufacturer.name.slice(0, 1).toUpperCase();
          const name = document.createElement('strong');
          name.textContent = manufacturer.name;
          button.append(mark, name);
          container.append(button);
        });
      }
      container.querySelectorAll('[data-designer-manufacturer]').forEach((button) => {
        button.setAttribute('aria-pressed', button.dataset.designerManufacturer === this.state.manufacturer ? 'true' : 'false');
      });
      this.querySelectorAll('[data-designer-wheel]').forEach((button) => {
        button.hidden = Boolean(this.state.manufacturer) && button.dataset.designerBrand !== this.state.manufacturer;
      });
    }

    selectManufacturer(handle) {
      this.state.manufacturer = handle;
      this.querySelectorAll('[data-designer-manufacturer]').forEach((button) => {
        button.setAttribute('aria-pressed', button.dataset.designerManufacturer === handle ? 'true' : 'false');
      });
      this.querySelectorAll('[data-designer-wheel]').forEach((button) => {
        button.hidden = button.dataset.designerBrand !== handle;
      });
      this.dispatchDesignerEvent('manufacturer-selected', { manufacturer: handle });
    }

    setStage(stage) {
      if (!['manufacturer', 'wheel', 'design'].includes(stage)) return;
      this.state.stage = stage;
      if (stage !== 'design') {
        this.state.hotspot = null;
        this.state.comparing = false;
      }
      this.renderStages();
      this.renderInteractivePreview();
    }

    renderStages() {
      this.dataset.activeStage = this.state.stage;
      this.stageTabs.forEach((tab) => {
        const current = tab.dataset.designerStage === this.state.stage;
        tab.classList.toggle('is-current', current);
        tab.setAttribute('aria-selected', current ? 'true' : 'false');
        tab.tabIndex = current ? 0 : -1;
      });
      this.stagePanels.forEach((panel) => {
        const current = panel.dataset.designerStagePanel === this.state.stage;
        panel.classList.toggle('is-current', current);
        panel.hidden = !current;
      });
    }

    selectProduct(handle) {
      const product = this.products.find(function (candidate) {
        return candidate.handle === handle;
      });
      if (!product) return;
      if (product.handle === this.config.product.handle) {
        this.replaceUrl();
        return;
      }

      this.config.product = product;
      this.state.manufacturer = slug(product.brand || '');
      const defaultVariant = product.variants.find(function (variant) {
        return variant.available && variant.featuredImage;
      }) || product.variants.find(function (variant) {
        return variant.available;
      }) || product.variants[0];
      this.state.options = defaultVariant.options.slice();
      this.state.variant = defaultVariant;
      this.state.invalidUrl = false;
      this.state.hotspot = null;
      this.state.comparing = false;
      this.rebuildProductControls();
      this.cacheElements();
      this.render({ replaceUrl: true });
      this.showMessage('');
      this.dispatchDesignerEvent('wheel-selected', { productHandle: product.handle });
    }

    rebuildProductControls() {
      const summaryList = this.querySelector('[data-designer-summary-options]');
      if (!summaryList) return;
      summaryList.replaceChildren();
      this.config.product.options.forEach((optionName, optionIndex) => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'scp-designer__summary-option';
        button.dataset.designerOptionTrigger = String(optionIndex);
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-controls', 'designer-option-panel-' + this.config.sectionId + '-' + optionIndex);
        const labelText = document.createElement('span');
        const selectedValue = document.createElement('strong');
        const chevron = document.createElement('span');
        labelText.textContent = optionName;
        selectedValue.dataset.designerSummaryOption = String(optionIndex);
        chevron.className = 'scp-designer__summary-chevron';
        chevron.setAttribute('aria-hidden', 'true');
        chevron.textContent = '›';
        button.append(labelText, selectedValue, chevron);
        const fieldset = document.createElement('fieldset');
        fieldset.className = 'scp-designer__option-group';
        fieldset.dataset.designerOptionGroup = String(optionIndex);
        fieldset.id = 'designer-option-panel-' + this.config.sectionId + '-' + optionIndex;
        fieldset.hidden = true;
        const legend = document.createElement('legend');
        legend.textContent = optionName;
        const grid = document.createElement('div');
        grid.className = 'scp-designer__option-grid';

        this.valuesForOption(optionIndex).forEach((value, valueIndex) => {
          const label = document.createElement('label');
          label.className = 'scp-designer__option-card';
          const input = document.createElement('input');
          input.type = 'radio';
          input.name = 'designer-option-' + this.config.sectionId + '-' + (optionIndex + 1);
          input.value = value;
          input.dataset.designerOption = String(optionIndex);
          input.id = 'designer-' + this.config.sectionId + '-' + (optionIndex + 1) + '-' + (valueIndex + 1);
          const inner = document.createElement('span');
          inner.className = 'scp-designer__option-card-inner';
          const swatch = document.createElement('span');
          swatch.className = 'scp-designer__swatch';
          swatch.setAttribute('aria-hidden', 'true');
          const name = document.createElement('span');
          name.className = 'scp-designer__option-name';
          name.textContent = value;
          inner.append(swatch, name);
          label.htmlFor = input.id;
          label.append(input, inner);
          grid.append(label);
        });

        fieldset.append(legend, grid);
        item.append(button, fieldset);
        summaryList.append(item);
      });

      const summaryProduct = this.querySelector('.scp-designer__summary-product');
      if (summaryProduct) summaryProduct.textContent = this.config.product.title;
      if (this.wheelNameElement) this.wheelNameElement.textContent = this.config.product.name || this.config.product.title;
      if (this.wheelNoteElement) {
        this.wheelNoteElement.textContent = this.config.product.compatibilityNote || '';
        this.wheelNoteElement.hidden = !this.wheelNoteElement.textContent;
      }
    }

    render(options) {
      const settings = options || {};
      this.state.variant = this.findVariant(this.state.options);
      this.renderOptions();
      this.renderSummary();
      this.renderPurchase();
      this.renderView();
      this.renderWheelSelector();
      this.renderStages();
      this.renderInteractivePreview();

      if (settings.replaceUrl) this.replaceUrl();
      if (settings.announceInvalidUrl) this.showMessage(this.config.strings.invalidUrl);
    }

    renderWheelSelector() {
      this.querySelectorAll('[data-designer-wheel]').forEach((button) => {
        const current = button.dataset.designerWheel === this.config.product.handle;
        button.classList.toggle('is-current', current);
        button.setAttribute('aria-pressed', current ? 'true' : 'false');
      });
      this.querySelectorAll('[data-designer-manufacturer]').forEach((button) => {
        button.setAttribute('aria-pressed', button.dataset.designerManufacturer === this.state.manufacturer ? 'true' : 'false');
      });
    }

    renderInteractivePreview() {
      this.querySelectorAll('[data-designer-option-trigger]').forEach((button) => {
        const current = Number(button.dataset.designerOptionTrigger) === this.state.hotspot;
        button.setAttribute('aria-expanded', current ? 'true' : 'false');
        button.classList.toggle('is-current', current);
      });
      this.hotspotButtons.forEach((button) => {
        const index = Number(button.dataset.designerHotspot);
        const current = index === this.state.hotspot;
        button.classList.toggle('is-current', current);
        button.setAttribute('aria-expanded', current ? 'true' : 'false');
        button.hidden = this.state.comparing || index >= this.config.product.options.length;
        const label = button.querySelector('.visually-hidden');
        if (label && this.config.product.options[index]) {
          label.textContent = this.config.strings.customiseOption.replace('__OPTION__', this.config.product.options[index]);
        }
      });
      this.querySelectorAll('[data-designer-option-group]').forEach((group) => {
        group.hidden = Number(group.dataset.designerOptionGroup) !== this.state.hotspot;
      });
      if (this.comparison) this.comparison.hidden = !this.state.comparing;
      if (this.comparisonToggle) this.comparisonToggle.setAttribute('aria-pressed', this.state.comparing ? 'true' : 'false');
    }

    renderOptions() {
      this.optionInputs.forEach((input) => {
        const index = Number(input.dataset.designerOption);
        const variant = this.nearestVariantForOption(index, input.value);
        const unavailable = !variant;

        input.checked = this.state.options[index] === input.value;
        input.disabled = unavailable;
        const card = input.closest('.scp-designer__option-card');
        card.classList.toggle('is-unavailable', unavailable);
        const swatch = card.querySelector('.scp-designer__swatch');
        const finish = (this.config.maskFinishes || []).find((candidate) =>
          candidate.productHandle === this.config.product.handle &&
          Number(candidate.optionIndex) === index && candidate.value === input.value
        );
        card.classList.toggle('has-finish', Boolean(finish));
        if (swatch) {
          swatch.style.backgroundColor = finish ? finish.color || '' : '';
          swatch.style.backgroundImage = finish && finish.texture ? 'url(' + JSON.stringify(finish.texture) + ')' : '';
        }
      });

      this.config.product.options.forEach((name, index) => {
        const target = this.querySelector('[data-designer-summary-option="' + index + '"]');
        if (target) target.textContent = this.state.options[index] || '';
      });
    }

    renderSummary() {
      const variant = this.state.variant;
      if (!variant) return;

      this.priceElement.textContent = this.formatMoney(variant.price);
      if (variant.compareAtPrice > variant.price) {
        this.comparePriceElement.textContent = this.formatMoney(variant.compareAtPrice);
        this.comparePriceElement.hidden = false;
      } else {
        this.comparePriceElement.hidden = true;
        this.comparePriceElement.textContent = '';
      }
    }

    renderPurchase() {
      const variant = this.state.variant;
      const available = Boolean(variant && variant.available);

      this.availabilityElement.textContent = variant
        ? (available ? this.config.strings.available : this.config.strings.soldOut)
        : this.config.strings.unavailable;
      this.addButton.disabled = !available || this.state.submitting;
      this.addButton.setAttribute('aria-busy', this.state.submitting ? 'true' : 'false');
      this.addLabel.textContent = this.state.submitting ? this.config.strings.adding : this.config.strings.add;
      this.addSpinner.hidden = !this.state.submitting;
    }

    renderView() {
      this.viewInputs.forEach((input) => {
        input.checked = input.value === this.state.view;
      });

      const image = this.resolvePreview();
      if (image) this.swapPreview(image);
      else {
        this.previewRequest++;
        this.displayedProductHandle = null;
        if (this.currentImage) this.currentImage.hidden = true;
        if (this.bufferImage) this.bufferImage.hidden = true;
        if (this.previewPlaceholder) this.previewPlaceholder.hidden = false;
        this.removeAttribute('data-preview-loading');
      }
      this.renderMaskPreview();
    }

    loadMaskAsset(src) {
      if (!this.maskAssets.has(src)) {
        const request = new Promise(function (resolve, reject) {
          const image = new Image();
          image.crossOrigin = 'anonymous';
          image.onload = function () { resolve(image); };
          image.onerror = reject;
          image.src = src;
        });
        this.maskAssets.set(src, request);
        request.catch(() => this.maskAssets.delete(src));
      }
      return this.maskAssets.get(src);
    }

    async loadMaskAlpha(src, width, height) {
      const key = src + ':' + width + 'x' + height;
      if (!this.maskAlphas.has(key)) {
        const request = this.loadMaskAsset(src).then(function (mask) {
          if (mask.naturalWidth !== width || mask.naturalHeight !== height) {
            throw new Error('Mask dimensions must match the base image');
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const context = canvas.getContext('2d', { willReadFrequently: true });
          if (!context) throw new Error('Canvas is unavailable');
          context.drawImage(mask, 0, 0);
          const pixels = context.getImageData(0, 0, width, height);
          for (let pixel = 0; pixel < pixels.data.length; pixel += 4) {
            const luminance = (pixels.data[pixel] + pixels.data[pixel + 1] + pixels.data[pixel + 2]) / 3;
            pixels.data[pixel + 3] = luminance * pixels.data[pixel + 3] / 255;
            pixels.data[pixel] = 255;
            pixels.data[pixel + 1] = 255;
            pixels.data[pixel + 2] = 255;
          }
          context.putImageData(pixels, 0, 0);
          return canvas;
        });
        this.maskAlphas.set(key, request);
        request.catch(() => this.maskAlphas.delete(key));
      }
      return this.maskAlphas.get(key);
    }

    async renderMaskPreview() {
      const requestId = ++this.maskRequest;
      const keepCurrentFrame = this.maskCanvas && !this.maskCanvas.hidden &&
        this.maskDisplayedHandle === this.config.product.handle && this.maskDisplayedView === this.state.view;
      if (this.maskCanvas && !keepCurrentFrame) this.maskCanvas.hidden = true;
      if (this.maskFeedback) this.maskFeedback.hidden = true;
      const scene = (this.config.maskScenes || []).find((candidate) =>
        candidate.productHandle === this.config.product.handle && candidate.base &&
        Array.isArray(candidate.masks) && candidate.masks.some(Boolean)
      );
      if (!scene || !this.maskCanvas || (this.state.view === 'installed' && this.config.settings.enableStudioView)) {
        if (this.maskCanvas) this.maskCanvas.hidden = true;
        this.dataset.maskStatus = 'inactive';
        return;
      }
      this.dataset.maskStatus = 'loading';

      try {
        const base = await this.loadMaskAsset(scene.base);
        const width = base.naturalWidth;
        const height = base.naturalHeight;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) throw new Error('Canvas is unavailable');
        context.drawImage(base, 0, 0);

        for (let index = 0; index < Math.min(scene.masks.length, this.state.options.length); index++) {
          const maskSource = scene.masks[index];
          const finish = (this.config.maskFinishes || []).find((candidate) =>
            candidate.productHandle === this.config.product.handle &&
            Number(candidate.optionIndex) === index && candidate.value === this.state.options[index]
          );
          if (!maskSource) continue;
          if (!finish) throw new Error('No mask finish matches the selected Shopify option value');
          const maskCanvas = await this.loadMaskAlpha(maskSource, width, height);

          const layer = document.createElement('canvas');
          layer.width = width;
          layer.height = height;
          const layerContext = layer.getContext('2d');
          if (!layerContext) throw new Error('Canvas is unavailable');
          if (finish.texture) {
            const texture = await this.loadMaskAsset(finish.texture);
            const pattern = layerContext.createPattern(texture, 'repeat');
            layerContext.fillStyle = pattern;
          } else {
            layerContext.fillStyle = finish.color || '#222222';
          }
          layerContext.fillRect(0, 0, width, height);
          layerContext.globalCompositeOperation = 'destination-in';
          layerContext.drawImage(maskCanvas, 0, 0);
          context.drawImage(layer, 0, 0);
        }

        if (scene.lighting) {
          const lighting = await this.loadMaskAsset(scene.lighting);
          if (lighting.naturalWidth !== width || lighting.naturalHeight !== height) throw new Error('Lighting dimensions must match the base image');
          context.drawImage(lighting, 0, 0);
        }
        if (requestId !== this.maskRequest) return;
        this.maskCanvas.width = width;
        this.maskCanvas.height = height;
        const displayContext = this.maskCanvas.getContext('2d');
        if (!displayContext) throw new Error('Canvas is unavailable');
        displayContext.drawImage(canvas, 0, 0);
        this.maskCanvas.setAttribute('aria-label', this.config.product.title);
        this.maskCanvas.hidden = false;
        this.maskDisplayedHandle = this.config.product.handle;
        this.maskDisplayedView = this.state.view;
        this.dataset.maskStatus = 'ready';
        if (this.previewPlaceholder) this.previewPlaceholder.hidden = true;
      } catch (error) {
        if (requestId === this.maskRequest) {
          this.maskCanvas.hidden = true;
          this.dataset.maskStatus = 'fallback';
          if (this.maskFeedback && window.Shopify && window.Shopify.designMode) {
            this.maskFeedback.textContent = this.config.strings.maskSetupError;
            this.maskFeedback.hidden = false;
          }
          if (this.debugEnabled()) console.warn('[SCP Designer] Mask preview fallback:', error);
        }
      }
    }

    resolvePreview() {
      const matchedPreview = (this.config.previews || []).find((preview) => {
        if (preview.productHandle && preview.productHandle !== this.config.product.handle) return false;
        const configuredOptions = preview.options.slice(0, this.state.options.length);
        return configuredOptions.every(function (value, index) {
          return !value || value === this.state.options[index];
        }, this);
      });

      if (matchedPreview && matchedPreview[this.state.view]) return matchedPreview[this.state.view];
      if (this.state.variant && this.state.variant.featuredImage) return this.state.variant.featuredImage;
      if (this.config.product.fallbackImages && this.config.product.fallbackImages[this.state.view]) {
        return this.config.product.fallbackImages[this.state.view];
      }
      return this.config.product.handle === this.config.settings.wheelHandle
        ? this.config.fallbackImages[this.state.view] || null
        : null;
    }

    async swapPreview(image) {
      if (!this.currentImage || !this.bufferImage || !image.src) return;
      const changingProduct = this.displayedProductHandle !== this.config.product.handle;
      if (changingProduct) {
        this.currentImage.hidden = true;
        if (this.previewPlaceholder) this.previewPlaceholder.hidden = false;
      } else {
        this.currentImage.hidden = false;
        if (this.previewPlaceholder) this.previewPlaceholder.hidden = true;
      }
      if (!changingProduct && (this.currentImage.currentSrc === image.src || (this.currentImage.getAttribute('src') === image.src && !this.currentImage.srcset))) {
        this.preloadLikelyImages();
        return;
      }

      const requestId = ++this.previewRequest;
      this.setAttribute('data-preview-loading', 'true');
      this.bufferImage.hidden = false;
      this.bufferImage.classList.remove('is-visible');
      this.bufferImage.src = image.src;
      this.bufferImage.alt = '';

      try {
        if (typeof this.bufferImage.decode === 'function') await this.bufferImage.decode();
        else await new Promise((resolve, reject) => {
          this.bufferImage.onload = resolve;
          this.bufferImage.onerror = reject;
        });

        if (requestId !== this.previewRequest) return;

        this.bufferImage.classList.add('is-visible');
        await this.waitForTransition();
        if (requestId !== this.previewRequest) return;

        this.currentImage.removeAttribute('srcset');
        this.currentImage.removeAttribute('sizes');
        this.currentImage.src = image.src;
        this.currentImage.alt = image.alt || this.config.product.title;
        this.currentImage.hidden = false;
        this.displayedProductHandle = this.config.product.handle;
        if (this.previewPlaceholder) this.previewPlaceholder.hidden = true;
        this.bufferImage.classList.remove('is-visible');
        this.preloaded.add(image.src);
        this.preloadLikelyImages();
      } catch (error) {
        if (requestId === this.previewRequest) {
          this.currentImage.hidden = true;
          this.bufferImage.hidden = true;
          this.bufferImage.classList.remove('is-visible');
          if (this.previewPlaceholder && (!this.maskCanvas || this.maskCanvas.hidden)) this.previewPlaceholder.hidden = false;
        }
      } finally {
        if (requestId === this.previewRequest) this.removeAttribute('data-preview-loading');
      }
    }

    waitForTransition() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return Promise.resolve();
      return new Promise(function (resolve) {
        window.setTimeout(resolve, 260);
      });
    }

    preloadLikelyImages() {
      if (navigator.connection && (navigator.connection.saveData || /(^|-)2g$/.test(navigator.connection.effectiveType || ''))) return;

      const urls = [];
      this.config.product.variants.forEach((variant) => {
        if (urls.length >= PRELOAD_LIMIT) return;
        const differenceCount = variant.options.reduce((count, value, index) => {
          return count + (value === this.state.options[index] ? 0 : 1);
        }, 0);
        const preview = variant.featuredImage;
        if (differenceCount === 1 && preview && !this.preloaded.has(preview.src)) urls.push(preview.src);
      });

      urls.slice(0, PRELOAD_LIMIT).forEach((url) => {
        const image = new Image();
        image.src = url;
        this.preloaded.add(url);
      });
    }

    replaceUrl() {
      if (!window.history || !window.history.replaceState) return;

      const url = new URL(window.location.href);
      this.config.product.options.map((name, index) => this.urlKeyForOption(name, index))
        .concat(['material', 'stripe', 'stitching', 'option-1', 'option-2', 'option-3'])
        .forEach(function (key) {
        url.searchParams.delete(key);
      });
      url.searchParams.set('wheel', this.config.product.handle);
      this.state.options.forEach((value, index) => {
        url.searchParams.set(this.urlKeyForOption(this.config.product.options[index], index), slug(value));
      });
      url.searchParams.delete('view');
      url.searchParams.set('designer_view', this.state.view);
      window.history.replaceState({ scpDesigner: true }, '', url.toString());
    }

    formatMoney(cents) {
      if (typeof window.formatMoney === 'function') {
        return window.formatMoney(cents, this.config.money.format);
      }

      if (typeof formatMoney === 'function') {
        return formatMoney(cents, this.config.money.format);
      }

      return (Number(cents || 0) / 100).toFixed(2);
    }

    async addToCart() {
      if (this.state.submitting || !this.state.variant || !this.state.variant.available) return;

      const purchaseGuard = window.SCPPurchaseContract;
      const purchaseDecision = purchaseGuard ? purchaseGuard.validate(this.config.product.purchaseContract, {
        id: this.state.variant.id, quantity: 1, properties: {}
      }, { fieldsReady: false }) : { ok: false, errors: ['guard_unavailable'] };
      if (this.state.invalidUrl || !purchaseDecision.ok) {
        this.showMessage(this.config.strings.purchaseReview);
        if (this.messageElement && this.config.product.url) {
          const reviewUrl = new URL(this.config.product.url, window.location.href);
          if (reviewUrl.origin === window.location.origin && reviewUrl.pathname.includes('/products/')) {
            if ((this.config.product.purchaseContract?.variantIds || []).map(String).includes(String(this.state.variant.id))) reviewUrl.searchParams.set('variant', this.state.variant.id);
            const reviewLink = document.createElement('a');
            reviewLink.href = reviewUrl.href;
            reviewLink.textContent = this.config.strings.purchaseReviewLink;
            this.messageElement.append(' ', reviewLink);
          }
        }
        this.dispatchDesignerEvent('purchase-review-required', { codes: purchaseDecision.errors });
        return;
      }
      const buildId = createBuildId();
      this.state.submitting = true;
      this.renderPurchase();
      this.showMessage('');
      this.dispatchDesignerEvent('add-started', { buildId: buildId });

      try {
        const response = await fetch(this.config.routes.cartAdd + '.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [{
              id: this.state.variant.id,
              quantity: 1,
              properties: {
                _scp_build_id: buildId,
                _scp_designer: 'steering-wheel-v1',
                Build: this.config.product.title,
                Component: 'Cover'
              }
            }],
            sections: ['mini-cart'],
            sections_url: window.location.pathname
          })
        });
        const responseJson = await response.json();

        if (!response.ok) {
          throw new Error(responseJson.description || this.config.strings.cartError);
        }

        const themeSettings = window.themeVariables && window.themeVariables.settings || {};
        if (themeSettings.cartType === 'page' || themeSettings.pageType === 'cart') {
          window.location.href = this.config.routes.cart;
          return;
        }

        const cartResponse = await fetch(this.config.routes.cart + '.js', {
          credentials: 'same-origin',
          cache: 'no-store'
        });
        const cart = await cartResponse.json();
        const items = responseJson.items || [];

        this.dispatchEvent(new CustomEvent('variant:added', {
          bubbles: true,
          detail: { variant: items[0] || responseJson, items: items, buildId: buildId }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: cart }
        }));
        cart.sections = responseJson.sections;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: cart,
            openMiniCart: themeSettings.cartType === 'drawer'
          }
        }));
        this.dispatchEvent(new CustomEvent('cart-notification:show', {
          bubbles: true,
          cancelable: true,
          detail: { status: 'success', error: '' }
        }));

        this.showMessage(this.config.strings.added);
        this.dispatchDesignerEvent('add-succeeded', { buildId: buildId, items: items.length || 1 });
      } catch (error) {
        const message = error && error.message ? error.message : this.config.strings.cartError;
        this.showMessage(message);
        this.dispatchEvent(new CustomEvent('cart-notification:show', {
          bubbles: true,
          cancelable: true,
          detail: { status: 'error', error: message }
        }));
        this.dispatchDesignerEvent('add-failed', { reason: 'cart-request' });
      } finally {
        this.state.submitting = false;
        this.renderPurchase();
      }
    }

    showMessage(message) {
      this.messageElement.textContent = message || '';
    }

    dispatchDesignerEvent(name, detail) {
      this.dispatchEvent(new CustomEvent('scp:designer:' + name, {
        bubbles: true,
        detail: detail || {}
      }));
    }

    debugEnabled() {
      const params = new URL(window.location.href).searchParams;
      return Boolean(window.Shopify && window.Shopify.designMode && params.get('designer_debug') === '1');
    }

    debugCatalog() {
      const duplicateHandles = [];
      this.config.product.options.forEach((name, index) => {
        const seen = new Set();
        this.valuesForOption(index).forEach((value) => {
          const handle = slug(value);
          if (seen.has(handle)) duplicateHandles.push(name + ': ' + handle);
          seen.add(handle);
        });
      });

      console.info('[SCP Designer]', {
        product: this.config.product.handle,
        variants: this.config.product.variants.length,
        previews: this.config.previews.length,
        duplicateHandles: duplicateHandles,
        state: this.state
      });
    }
  }

  if (!window.customElements.get(ELEMENT_NAME)) {
    window.customElements.define(ELEMENT_NAME, SCPDesigner);
  }
})();
